import React, { useContext, useMemo, useState } from "react";
import {
  absolutifyImgs,
  base64ifyLogos,
  buildPrintHTML,
  PAGE_H,
  PAGE_W,
  toBase64FromPublic,
} from "../../../../utils/PDFUtils";

import { formatDate } from "../../../../utils/DateFormat";
import Loading from "../../components/Loading";
import MobilePatternHeader from "../../components/MobilePatternHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TicketPopup from "../popup/TicketPopup";
import { EstimatePopup } from "../popup/EstimatePopup";
import { ReactComponent as MoneyIcon } from "../../../../assets/icons/money.svg";

import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { listFileService } from "../../../../services/api/listFileService";
import DesktopProfileContent from "../components/DesktopProfileContent";
import { getBackendUrl } from "../../../../utils/env";

const PLACEHOLDER = "/images/default-profile.png";

const loadProfilePdfFonts = async () => {
  try {
    const [profileFontRegularBase64, profileFontBoldBase64] =
      await Promise.all([
        toBase64FromPublic("/fonts/BeVietnamPro-Medium.ttf"),
        toBase64FromPublic("/fonts/BeVietnamPro-SemiBold.ttf"),
      ]);

    return { profileFontRegularBase64, profileFontBoldBase64 };
  } catch (error) {
    console.error("Unable to load profile PDF fonts:", error);
    return {};
  }
};

function formatValue(data) {
  return data.includes("Nhóm") ? data : "Nhóm " + data;
}

const DynamicForm = ({
  documentData,
  process = [],
  data,
  represent,
  category,
  isMobile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { token_client } = useContext(AuthContext);
  const source = new URLSearchParams(location.search).get("from");
  const isFromAssetList = source === "asset";
  const assetReturnUrl = `/danh-muc-tai-san-tri-tue${location.state?.assetReturnSearch || ""}`;
  const handleBackToAssetList = () => navigate(assetReturnUrl);

  const [estimateLoading, setEstimateLoading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const [estimateData, setEstimateData] = useState(null);
  const [isEstimateOpen, setIsEstimateOpen] = useState(false);

  const handleClosePopup = () => setIsPopupOpen(false);
  const handleOpenPopup = () => setIsPopupOpen(true);

  const normalizeGroupLabel = (v) =>
    String(v || "").includes("Nhóm") ? v : `Nhóm ${v}`;

  const isArray = (v) => Array.isArray(v);
  const hiddenFieldClass = useMemo(() => "hide-section", []);

  const renderSection = (section, index, compact = false) => {
    const hasFileOrImage =
      isArray(section?.fields) &&
      section.fields.some(
        (f) =>
          f.fieldType === "pdf" ||
          f.fieldType === "image" ||
          f.fieldType === "media-group",
      );
    const sectionClass = hasFileOrImage ? hiddenFieldClass : "";

    const Title = () =>
      compact ? (
        <h4 className="text-[14px] text-[#0267FF] font-sf-medium">
          {index + 1}. {section.type}
        </h4>
      ) : (
        <h4 className="text-md font-sf-semibold uppercase">
          {index + 1}. {section.type}
        </h4>
      );

    const specialTypes = [
      "Danh mục hàng hóa/dịch vụ",
      "Nội dung yêu cầu tra cứu",
      "Kiểu dáng công nghiệp",
      "Sáng chế/giải pháp hữu ích",
      "Tác phẩm",
    ];

    if (section.type === "Tài liệu gửi kèm (nếu có)") {
      return (
        <div key={`sec-${index}`}>
          {represent && (
            <div className={`space-y-1 my-6 ${isPrinting ? "mb-24" : ""}`}>
              {compact ? (
                <h4 className="text-[14px] text-[#0267FF] font-sf-medium">
                  {index + 1}. Tổ chức đại diện SHCN
                </h4>
              ) : (
                <h4 className="text-md font-sf-semibold uppercase">
                  {index + 1}. Tổ chức đại diện SHCN
                </h4>
              )}
              <div className={`grid grid-cols-1 gap-2`}>
                {compact ? (
                  <>
                    <div className="flex items-start text-[14px]">
                      <p className="whitespace-nowrap mr-2">Tên tổ chức:</p>
                      <p className="font-sf-semibold">
                        {represent.companyName}
                      </p>
                    </div>
                    <div className="flex items-start text-[14px]">
                      <p className="whitespace-nowrap mr-2">Địa chỉ:</p>
                      <p className="font-sf-semibold">{represent.address}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <p>{represent.companyName}</p>
                    <p>{represent.address}</p>
                  </>
                )}
              </div>
            </div>
          )}

          <div
            className={`mb-4 ${sectionClass} flex-col`}
            key={`files-${index}`}
          >
            {compact ? (
              <h4 className="text-[14px] text-[#0267FF] font-sf-medium">
                {index + 2}. {section.type}
              </h4>
            ) : (
              <h4 className="text-md font-sf-semibold uppercase">
                {index + 2}. {section.type}
              </h4>
            )}

            {section.fields?.map((field, fi) => {
              const isLink =
                typeof field.value === "string" ||
                (isArray(field.value) && field.value.length > 0);
              const label = compact
                ? "text-blue-500 hover:text-blue-600 hover:underline font-sf-semibold"
                : "text-blue-500 hover:text-blue-600 hover:underline";

              return (
                <div
                  className={`flex items-start ${compact ? "text-[14px]" : ""}`}
                  key={`f-${fi}`}
                >
                  <strong className={`${compact ? "mr-2" : "mr-2"}`}>
                    {field.name}:
                  </strong>
                  {!isLink ? (
                    <span>
                      {isArray(field.value)
                        ? "Không có tài liệu đính kèm !"
                        : "Không có tài liệu đính kèm !"}
                    </span>
                  ) : typeof field.value === "string" ? (
                    <a
                      className={label}
                      href={field.value}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {field.fieldType === "image" ? "Hình ảnh" : "Tài liệu"}
                    </a>
                  ) : (
                    <span>
                      {field.value.map((val, i) => (
                        <span key={i}>
                          <a
                            className={label}
                            href={val}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Tài liệu
                          </a>
                          {i < field.value.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={`mb-4 ${sectionClass}`} key={`sec-${index}`}>
        <Title />
        {specialTypes.includes(section.type) ? (
          <div
            className={`grid grid-cols-1 gap-2 ${compact ? "text-[14px]" : ""}`}
          >
            {section.fields?.map((field, fi) => {
              if (fi % 2 === 0 && section.fields[fi + 1]) {
                const label = normalizeGroupLabel(section.fields[fi].value);
                const value = section.fields[fi + 1].value;
                return (
                  <div key={`pair-${fi}`} className="flex items-start w-full">
                    <div
                      className={`${
                        compact ? "" : "font-semibold"
                      } whitespace-nowrap mr-2`}
                    >
                      {label}:
                    </div>
                    <div className="text-left break-all w-full">{value}</div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        ) : (
          section.fields?.map((field, fi) => {
            return (
              <div
                className={`flex items-start ${compact ? "text-[14px]" : ""}`}
                key={`nf-${fi}`}
              >
                <strong className="mr-2 whitespace-nowrap">
                  {field.name}:
                </strong>
                <span>{field.value}</span>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderContent = (data) => {
    if (!data) return null;
    if (!Array.isArray(data)) {
      return <div>Không có dữ liệu form đã nhập</div>;
    }

    return (
      <div className="my-6 space-y-6">
        {data.map((section, index) => {
          const hasFileOrImage =
            Array.isArray(section.fields) &&
            section.fields.some(
              (field) =>
                field.fieldType === "pdf" ||
                field.fieldType === "image" ||
                field.fieldType === "media-group",
            );
          const sectionClass = hasFileOrImage ? "hide-section" : "";

          if (section.type === "Tài liệu gửi kèm (nếu có)") {
            return (
              <div key={index}>
                {represent && (
                  <div className="space-y-1 my-6">
                    <h4 className="text-md font-sf-semibold uppercase">
                      {index + 1}. Tổ chức đại diện SHCN
                    </h4>
                    <div className={`grid grid-cols-1 gap-2`}>
                      <p className="text-left break-all w-full">
                        {represent.companyName}
                      </p>
                      <p className="text-left break-all w-full">
                        {represent.address}
                      </p>
                    </div>
                  </div>
                )}
                <div className={`mb-4 ${sectionClass} flex-col`}>
                  <h4 className="text-md font-sf-semibold uppercase">
                    {index + 2}. {section.type}
                  </h4>

                  {section.fields.map((field, fieldIndex) => {
                    const fieldClass =
                      field.fieldType === "pdf" || field.fieldType === "image"
                        ? `field-${field.fieldType}`
                        : "";

                    return (
                      <div
                        className={`flex items-center ${fieldClass}`}
                        key={fieldIndex}
                      >
                        <strong className="mr-2">{field.name}:</strong>
                        {!field.value ||
                        (Array.isArray(field.value) &&
                          field.value.length === 0) ? (
                          <p>Không có tài liệu đính kèm !</p>
                        ) : typeof field.value === "string" ? (
                          <a
                            className="text-blue-500 hover:text-blue-600 hover:underline"
                            href={field.value}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {field.fieldType === "image"
                              ? "Hình ảnh"
                              : "Tài liệu"}
                          </a>
                        ) : (
                          Array.isArray(field.value) && (
                            <span>
                              {field.value.map((field_value, idx) => (
                                <span key={idx}>
                                  <a
                                    className="text-blue-500 hover:text-blue-600 hover:underline"
                                    href={field_value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {"Tài liệu"}
                                  </a>
                                  {idx < field.value.length - 1 ? ", " : ""}
                                </span>
                              ))}
                            </span>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          } else {
            return (
              <div className={`mb-4 ${sectionClass}`} key={index}>
                <h4 className="text-md font-sf-semibold uppercase">
                  {index + 1}. {section.type}
                </h4>

                {section.type === "Danh mục hàng hóa/dịch vụ" ||
                section.type === "Nội dung yêu cầu tra cứu" ||
                section.type === "Kiểu dáng công nghiệp" ||
                section.type === "Sáng chế/giải pháp hữu ích" ||
                section.type === "Tác phẩm" ? (
                  <div className="grid grid-cols-1 gap-2">
                    {section.fields.map((field, fieldIndex) => {
                      if (
                        fieldIndex % 2 === 0 &&
                        section.fields[fieldIndex + 1]
                      ) {
                        const label = formatValue(field.value);
                        const value = section.fields[fieldIndex + 1].value;

                        return (
                          <div
                            key={fieldIndex}
                            className="flex items-start w-full"
                          >
                            <div className="font-semibold whitespace-nowrap mr-2">
                              {label}:
                            </div>
                            <div className="text-left break-all w-full">
                              {value}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                ) : (
                  section.fields.map((field, fieldIndex) => {
                    const isPhone =
                      field.name === "Số điện thoại" &&
                      section.fields[fieldIndex + 1]?.name === "Email";
                    const isEmail =
                      field.name === "Email" &&
                      section.fields[fieldIndex - 1]?.name === "Số điện thoại";
                    if (isPhone) {
                      return (
                        <div
                          key={fieldIndex}
                          className="profile-contact-row flex items-start space-x-6"
                        >
                          <div className="profile-contact-item flex items-start">
                            <strong className="mr-2 whitespace-nowrap">
                              {field.name}:
                            </strong>
                            <span className="text-left break-all w-full">
                              {field.value}
                            </span>
                          </div>
                          <div className="profile-contact-item flex items-start">
                            <strong className="mr-2 whitespace-nowrap">
                              {section.fields[fieldIndex + 1].name}:
                            </strong>
                            <span className="text-left break-all w-full">
                              {section.fields[fieldIndex + 1].value}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    if (isEmail) return null;
                    return (
                      <div className={`flex items-start `} key={fieldIndex}>
                        <strong className="mr-2 whitespace-nowrap">
                          {field.name}:
                        </strong>
                        <span className="text-left break-all w-full">
                          {field.value}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            );
          }
        })}
      </div>
    );
  };

  const renderContentMobile = (arr) => {
    if (!arr) return null;
    if (!Array.isArray(arr)) return <div>Không có dữ liệu form đã nhập</div>;
    return (
      <div className="my-6 space-y-6 motion-safe:animate-[fadeIn_260ms_ease-out]">
        {arr.map((section, idx) => renderSection(section, idx, true))}
      </div>
    );
  };

  const convertProfileName = (name) => {
    if (!name) return "";
    let n = String(name).toLowerCase();
    if (n.startsWith("đăng ký")) n = n.substring(8).trim();
    return `Hồ sơ ${n}`;
  };

  const handelDuplicateProfile = async () => {
    try {
      setLoading(true);
      const response = await listFileService.duplicate_profile(
        id,
        token_client,
      );
      navigate(`/quan-ly-ho-so/ho-so/${response.profile._id}/chinh-sua`);
      toast.success(
        `Đã nhân bản ${convertProfileName(
          data?.profile_name,
        ).toLowerCase()} thành công!`,
      );
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response
          ? "Lỗi từ server: " +
              (error.response.data?.message || "Không xác định")
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Nhân bản lỗi:", error);
    } finally {
      setLoading(false);
    }
  };

  async function handlePrint() {
    try {
      setIsPrinting(true);

      const original = document.getElementById("contentToPrint");
      if (!original) throw new Error("Content to print not found");

      const cloned = original.cloneNode(true);
      absolutifyImgs(cloned);
      await base64ifyLogos(cloned, "/images/default-profile.png");

      const watermarkMode = "fixed";
      const profilePdfFonts = await loadProfilePdfFonts();
      const htmlContent = buildPrintHTML(
        cloned.innerHTML,
        watermarkMode,
        profilePdfFonts,
      );
      const watermarkBase64 = await toBase64FromPublic(
        "/images/watermark-45.png",
      );

      const BE_URL = getBackendUrl();

      const response = await fetch(`${BE_URL}/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token_client}`,
        },
        body: JSON.stringify({
          html: htmlContent,
          profileName: convertProfileName(data && data.profile_name),
          watermarkBase64,
          watermarkOpacity: 0.12,
          options: {
            width: PAGE_W,
            height: PAGE_H,
            deviceScaleFactor: 2,
          },
          watermarkMode,
        }),
      });

      if (!response.ok) {
        let msg = "Failed to generate PDF";
        try {
          msg = (await response.json()).message || msg;
        } catch (e) {}
        throw new Error(msg);
      }

      const ct = response.headers.get("content-type") || "";
      if (!ct.includes("application/pdf")) {
        const text = await response.text();
        console.error("Unexpected content-type:", ct, text.slice(0, 200));
        throw new Error("Server did not return a PDF");
      }

      const buf = await response.arrayBuffer();
      const blob = new Blob([buf], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        (convertProfileName(data && data.profile_name) || "document") + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Tải PDF thành công!");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.dismiss();
      toast.error((err && err.message) || "Lỗi không tạo được file PDF");
    } finally {
      setIsPrinting(false);
    }
  }

  const handleCloseEstimate = () => {
    setIsEstimateOpen(false);
    setEstimateData(null);
  };

  const handleOpenEstimate = async () => {
    try {
      setEstimateLoading(true);
      setIsEstimateOpen(true);
      const res = await listFileService.get_profile_estimate(token_client, id);
      setEstimateData(res?.data || null);
    } catch (err) {
      console.error("Lỗi lấy báo phí:", err);
      toast.error("Không thể lấy báo phí hồ sơ");
      setIsEstimateOpen(false);
    } finally {
      setEstimateLoading(false);
    }
  };

  if (!documentData && !process && !data) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <div
          className="bg-white relative h-full overflow-auto hidden"
          id="contentToPrint"
        >
          <div className="bg-white relative h-full overflow-auto rounded-lg px-[10vw] pb-5">
            <DesktopProfileContent
              documentData={documentData}
              process={process}
              data={data}
              represent={represent}
              category={category}
              convertProfileName={convertProfileName}
              renderContent={renderContent}
              isPrinting={isPrinting}
              handlePrint={handlePrint}
              handleOpenPopup={handleOpenPopup}
              handelDuplicateProfile={handelDuplicateProfile}
              isPopupOpen={isPopupOpen}
              handleClosePopup={handleClosePopup}
              id={id}
              estimateLoading={estimateLoading}
              handleOpenEstimate={handleOpenEstimate}
              isEstimateOpen={isEstimateOpen}
              handleCloseEstimate={handleCloseEstimate}
              estimateData={estimateData}
              getAdjustmentHistory={
                listFileService.get_estimate_adjustment_history
              }
              isFromAssetList={isFromAssetList}
              onBackToAssetList={handleBackToAssetList}
            />
          </div>
        </div>

        <div className="w-full h-full">
          <MobilePatternHeader
            title="Chi tiết hồ sơ"
            onBack={isFromAssetList ? handleBackToAssetList : () => navigate(-1)}
          />

          {isPrinting && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
              <div className="bg-white px-6 py-4 rounded-lg shadow-lg text-lg font-semibold">
                Đang tạo PDF, vui lòng đợi...
                <div className="mt-3 flex justify-center">
                  <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
            </div>
          )}

          <div className="h-[calc(100%-110px)] pb-6 overflow-auto bg-[#F2F2F2]">
            <div className="h-full overflow-auto px-3">
              <div className="bg-white rounded-lg p-3 mt-3 motion-safe:animate-[fadeIn_260ms_ease-out]">
                <h1 className="w-full text-center font-sf-semibold text-[#0457C2] uppercase border-b p-3 pt-0">
                  {convertProfileName(data?.profile_name)}
                </h1>

                <div className="mt-3">
                  <div className="flex flex-col gap-4 text-[14px]">
                    {[
                      ["Số đơn:", data?.soDon || "--"],
                      ["Số bằng:", data?.soBang || "--"],
                      [
                        "Ngày nộp đơn:",
                        data?.createDate ? formatDate(data.createDate) : "--",
                      ],
                      [
                        "Ngày công bố:",
                        data?.dateActive ? formatDate(data.dateActive) : "--",
                      ],
                      [
                        "Ngày cấp bằng:",
                        data?.issueDate ? formatDate(data.issueDate) : "--",
                      ],
                      [
                        "Ngày hết hạn:",
                        data?.expiryDate ? formatDate(data.expiryDate) : "--",
                      ],
                      [
                        category === "Nhãn hiệu" ? "Nhãn hiệu:" : "Tên hồ sơ:",
                        data?.brand || "",
                      ],
                    ].map(([label, value], idx) => (
                      <div key={idx} className="flex items-center">
                        <p className="mr-2 whitespace-nowrap">{label}</p>
                        <span className="font-sf-semibold whitespace-nowrap truncate">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="w-full flex items-center justify-center flex-wrap my-3 gap-4">
                    {Array.isArray(data?.profile_image) ? (
                      data.profile_image.length > 0 ? (
                        data.profile_image.map((img, i) => (
                          <img
                            key={i}
                            src={img?.value || PLACEHOLDER}
                            alt="So huu tri tue logo"
                            className="profile-logo sm:w-fit w-full sm:h-[7.5rem] max-h-[100px] object-contain"
                            onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                          />
                        ))
                      ) : (
                        <img
                          src={PLACEHOLDER}
                          alt="So huu tri tue logo"
                          className="profile-logo sm:w-fit w-full sm:h-[7.5rem] max-h-[100px] object-contain"
                        />
                      )
                    ) : (
                      <img
                        src={data?.profile_image || PLACEHOLDER}
                        alt="So huu tri tue logo"
                        className="profile-logo sm:w-fit w-full sm:h-[7.5rem] max-h-[100px] object-contain"
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                      />
                    )}
                  </div>

                  {renderContentMobile(documentData)}

                  {(data?.isLookup || data?.isRequire || data?.isOriginal) && (
                    <div className="mt-8 space-y-3 bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                      <h4 className="font-sf-semibold text-[#0457C2] uppercase text-sm tracking-wide">
                        Thông tin bổ sung
                      </h4>
                      <div className="flex flex-col gap-3">
                        {data?.isOriginal && (
                          <div className="flex items-center gap-3">
                            <div className="size-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-200"></div>
                            <span className="font-sf-medium text-gray-700">
                              Hồ sơ gốc tài sản trí tuệ
                            </span>
                          </div>
                        )}
                        {data?.isLookup && (
                          <div className="flex items-center gap-3">
                            <div className="size-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-200"></div>
                            <span className="font-sf-medium text-gray-700">
                              Có yêu cầu Tra cứu
                            </span>
                          </div>
                        )}
                        {data?.isRequire && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="size-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-200"></div>
                              <span className="font-sf-medium text-gray-700">
                                Có yêu cầu khác
                              </span>
                            </div>
                            {data?.contentRequire && (
                              <div className="ml-5.5 p-4 bg-white/80 rounded-lg border border-blue-50 text-sm text-gray-700 italic leading-relaxed shadow-sm">
                                {data.contentRequire.replace(/^"|"$/g, "")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white border rounded-lg overflow-hidden mt-3 text-[14px]">
                    <h4
                      className={`text-white font-sf-medium bg-[#0457C2] px-3 ${
                        isPrinting ? "pb-6" : "py-3"
                      }`}
                    >
                      Tiến trình hồ sơ
                    </h4>
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-200 h-[60px]">
                          <th className="px-3 py-3 w-[60px]">STT</th>
                          <th className="px-3 py-3">Nội dung</th>
                          <th className="px-3 py-3">Ngày</th>
                          <th className="px-5 py-3 text-right">File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {process?.length > 0 &&
                          process.map((item, index) => (
                            <tr
                              key={index}
                              className="hover:bg-blue-50 border-b last:border-0"
                            >
                              <td className="p-3">{index + 1}</td>
                              <td className="p-3">{item?.processContent}</td>
                              <td className="p-3">
                                {formatDate(item?.completionDate)}
                              </td>
                              <td className="p-3 text-blue-600 text-right">
                                {item?.pdfUrl ? (
                                  <a
                                    href={item.pdfUrl}
                                    className="hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Tài liệu
                                  </a>
                                ) : (
                                  "--"
                                )}
                              </td>
                            </tr>
                          ))}
                        {(!process || process.length === 0) && (
                          <tr>
                            <td
                              colSpan={4}
                              className="p-4 text-center text-gray-400 italic"
                            >
                              Chưa có tiến trình nào được ghi nhận
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div
                    id="download-btn"
                    className="w-full flex items-center justify-center flex-wrap gap-3 my-5"
                  >
                    {data?.status?.value === "Chờ duyệt" && (
                      <button
                        onClick={() =>
                          navigate(`/quan-ly-ho-so/ho-so/${id}/chinh-sua`)
                        }
                        className="flex items-center justify-center border bg-blue-500 border-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 px-[10px] gap-[5px] active:scale-[0.99]"
                      >
                        <img src="/icons/edit.svg" alt="Edit icon" />
                        Chỉnh sửa hồ sơ
                      </button>
                    )}
                    <button
                      onClick={handlePrint}
                      className="flex items-center justify-center border border-[#0457C2] rounded-lg py-2 px-[10px] gap-[5px] active:scale-[0.99]"
                    >
                      <img src="/icons/download.svg" alt="Download icon" />
                      Tải hồ sơ
                    </button>
                    <button
                      onClick={handleOpenEstimate}
                      className="flex items-center justify-center border border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white rounded-lg py-2 px-[10px] gap-[5px] disabled:opacity-50 disabled:cursor-default active:scale-[0.99]"
                    >
                      <MoneyIcon className="w-5 h-5 shrink-0" />
                      Chi phí
                    </button>
                    {data?.status?.value === "Đang triển khai" && (
                      <button
                        onClick={handleOpenPopup}
                        className="flex items-center justify-center border bg-blue-500 border-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 px-[10px] gap-[5px] active:scale-[0.99]"
                      >
                        <img src="/icons/ticket.svg" alt="ticket icon" />
                        Gửi yêu cầu
                      </button>
                    )}
                    <button
                      onClick={handelDuplicateProfile}
                      className="flex items-center justify-center border bg-[#0457C2] border-[#0457C2] text-white hover:bg-blue-600 rounded-lg py-2 px-[10px] gap-[5px] active:scale-[0.99]"
                    >
                      <img src="/icons/copy.svg" alt="Copy icon" />
                      Nhân bản hồ sơ
                    </button>
                  </div>

                  {isPopupOpen && (
                    <TicketPopup
                      isOpen={isPopupOpen}
                      onClose={handleClosePopup}
                      profileID={id}
                      profileName={convertProfileName(data?.profile_name)}
                    />
                  )}

                  {isEstimateOpen && estimateData && (
                    <EstimatePopup
                      isOpen={isEstimateOpen}
                      onClose={handleCloseEstimate}
                      data={estimateData}
                      getAdjustmentHistory={
                        listFileService.get_estimate_adjustment_history
                      }
                      estimateLoading={estimateLoading}
                    />
                  )}

                  <p className="w-full text-center text-[#333333] mt-8 mb-5 text-[13px] leading-[1.35]">
                    Hồ sơ được tải về ngày{" "}
                    {new Date().toLocaleDateString("vi-VN")} từ Nền tảng số
                    Sohuutritue.vn
                    <br />
                    Tổng đài tư vấn Sở hữu trí tuệ:{" "}
                    <a href="tel:0819009697" className="hover:underline">
                      08 1900 9697
                    </a>{" "}
                    (điện thoại, zalo) - Email:{" "}
                    <a
                      href="mailto:luatsu@sohuutritue.vn"
                      className="text-blue-600 hover:underline"
                    >
                      luatsu@sohuutritue.vn
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
        </style>
      </>
    );
  }

  // Desktop
  return (
    <>
      {isPrinting && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg text-lg font-semibold">
            Đang tạo PDF, vui lòng đợi...
            <div className="mt-3 flex justify-center">
              <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        </div>
      )}
      <div
        className="relative h-full overflow-auto flex flex-col gap-6 w-full bg-white rounded-[16px] border border-[#D2D5DA] p-4"
        id="contentToPrint"
      >
        <div className="bg-white rounded-[16px] p-4 flex flex-col gap-4 profile-container">
          <DesktopProfileContent
            documentData={documentData}
            process={process}
            data={data}
            represent={represent}
            category={category}
            convertProfileName={convertProfileName}
            renderContent={renderContent}
            isPrinting={isPrinting}
            handlePrint={handlePrint}
            handleOpenPopup={handleOpenPopup}
            handelDuplicateProfile={handelDuplicateProfile}
            isPopupOpen={isPopupOpen}
            handleClosePopup={handleClosePopup}
            id={id}
            estimateLoading={estimateLoading}
            handleOpenEstimate={handleOpenEstimate}
            isEstimateOpen={isEstimateOpen}
            handleCloseEstimate={handleCloseEstimate}
            estimateData={estimateData}
            getAdjustmentHistory={
              listFileService.get_estimate_adjustment_history
            }
            isFromAssetList={isFromAssetList}
            onBackToAssetList={handleBackToAssetList}
          />
        </div>
      </div>
    </>
  );
};

export default DynamicForm;
